import { Host, h } from "@stencil/core";
import { SCROLLABLE_CLASS } from "../../common/reserved-names";
import { adoptCommonThemes } from "../../common/theme";
import { calculateSpaceToReserve } from "./calculate-space-to-reserve";
const HIDE_CONTENT_AFTER_LOADING_CLASS = "ch-smart-grid--loaded-render-delay";
const SMART_GRID_CELL_TAG_NAME = "ch-smart-grid-cell";
const RESERVED_SPACE_CLASS_NAME = "ch-smart-grid-cell-reserve-space";
const RESERVED_SPACE_CUSTOM_VAR = "--ch-smart-grid-smart-cell-reserved-space-size";
export class ChSmartGrid {
    constructor() {
        this.infiniteScrollEnabled = true;
        this.cellRefAlignedAtTheTop = null;
        this.accessibleName = undefined;
        this.autoGrow = false;
        this.autoScroll = "at-scroll-end";
        this.dataProvider = false;
        this.inverseLoading = false;
        this.itemsCount = undefined;
        this.loadingState = "initial";
        this.threshold = "10px";
    }
    #lastCellRef = null;
    cellRefAlignedAtTheTopChanged(newValue, oldValue) {
        this.#reserveSpaceInLastCellToKeepAnchorCellAlignedAtTheStart();
        // Connect watcher to keep the anchor cell aligned
        if (oldValue === null) {
            this.el.addEventListener("smartCellDidLoad", this.#reserveSpaceInLastCellToKeepAnchorCellAlignedAtTheStart);
            this.el.addEventListener("smartCellDisconnectedCallback", this.#observeCellRemovals);
        }
        // The anchor cell no longer exists, remove watcher
        else if (newValue === null) {
            this.el.removeEventListener("smartCellDidLoad", this.#reserveSpaceInLastCellToKeepAnchorCellAlignedAtTheStart);
            this.el.removeEventListener("smartCellDisconnectedCallback", this.#observeCellRemovals);
        }
    }
    /**
     * This variable is used to avoid layout shifts (CLS) at the initial load,
     * due to the async render of the content.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #contentIsHidden = false;
    el;
    /**
     * This Handler will be called every time grid threshold is reached. Needed
     * for infinite scrolling grids.
     */
    infiniteThresholdReached;
    /**
     * Given the cell ID, it position the item at the start or end of the
     * scrollbar.
     *
     * If `position === "start"` it will reserve the necessary space to visualize
     * the item at the start of the `ch-smart-grid` viewport if the content is
     * not large enough.
     * This behavior is the same as the Monaco editor does for reserving space
     * when visualizing the last lines positioned at the top of the editor.
     */
    async scrollEndContentToPosition(cellId, options) {
        const cellRef = this.#getCellById(cellId);
        this.cellRefAlignedAtTheTop = options.position === "start" ? cellRef : null;
        if (cellRef) {
            // Since the space reservation is performed in a rAF, we have to perform
            // the scroll repositioning in the same frame calling rAF
            requestAnimationFrame(() => setTimeout(() => this.el.scrollBy({
                top: cellRef.offsetTop,
                behavior: options.behavior ?? "auto"
            }))
            // 100
            );
        }
    }
    // TODO: This method should probably not exists. We should find a better way
    // to implement this.
    /**
     * Removes the cell reference that is aligned at the start of the viewport.
     *
     * In other words, removes the reserved space that is used to aligned
     * `scrollEndContentToPosition(cellId, { position: "start" })`
     */
    async removeScrollEndContentReference() {
        this.cellRefAlignedAtTheTop = null;
    }
    handleVirtualItemsChanged(event) {
        const { startIndex, endIndex, totalItems } = event.detail;
        this.infiniteScrollEnabled =
            (this.inverseLoading && startIndex === 0) ||
                (!this.inverseLoading && endIndex === totalItems - 1);
    }
    #getCellById = (cellId) => this.el.querySelector(`${SMART_GRID_CELL_TAG_NAME}[cell-id="${cellId}"]`);
    #infiniteThresholdReachedCallback = () => {
        this.loadingState = "loading";
        this.infiniteThresholdReached.emit();
    };
    #avoidCLSOnInitialLoad = () => {
        if (this.inverseLoading) {
            this.#contentIsHidden = true;
            this.el.classList.add(HIDE_CONTENT_AFTER_LOADING_CLASS);
        }
    };
    #removeAvoidCLS = () => {
        this.#contentIsHidden = false;
        this.el.removeEventListener("virtualScrollerDidLoad", this.#removeAvoidCLS);
        requestAnimationFrame(() => this.el.classList.remove(HIDE_CONTENT_AFTER_LOADING_CLASS));
    };
    #checkIfAnchorWasRemoved = () => {
        if (this.cellRefAlignedAtTheTop === null) {
            this.el.style.removeProperty(RESERVED_SPACE_CUSTOM_VAR);
            this.#lastCellRef = null;
            return true;
        }
        return false;
    };
    #reserveSpaceInLastCellToKeepAnchorCellAlignedAtTheStart = () => {
        // Remove min-block-size from the last cell, since the anchor cell doesn't
        // exists
        if (this.#checkIfAnchorWasRemoved()) {
            return;
        }
        // We have to wait until the data-did-load attr is attached to the rendered
        // cell in order to properly calculate the distance to the DOM, since when
        // the cell doesn't have that attribute in the virtual-scroller scenario,
        // it has "display: none" to avoid any flickering
        setTimeout(() => {
            // We have to check this condition here too, since the timeout can be
            // queued before the removal of the anchor and then executed after the
            // anchor has been removed
            if (this.#checkIfAnchorWasRemoved()) {
                return;
            }
            // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
            const newLastCell = this.el.querySelector(
            // TODO: This is a WA to make work the ch-chat with the Lit render.
            // This WA won't be necessary when we fully migrate the ch-chat to Lit
            `:scope > [slot="grid-content"] > ch-chat-lit > ${SMART_GRID_CELL_TAG_NAME}:last-child`
            // `:scope > [slot="grid-content"] > ${SMART_GRID_CELL_TAG_NAME}:last-child`
            );
            if (this.#lastCellRef !== newLastCell) {
                const newSize = newLastCell
                    ? calculateSpaceToReserve(this.el, this.cellRefAlignedAtTheTop, newLastCell)
                    : 0;
                // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
                this.el.style.removeProperty(RESERVED_SPACE_CUSTOM_VAR);
                this.#lastCellRef?.classList.remove(RESERVED_SPACE_CLASS_NAME);
                this.#lastCellRef = newLastCell;
                // TODO: Properly recalculate
                if (newLastCell) {
                    newLastCell.classList.add(RESERVED_SPACE_CLASS_NAME);
                    this.el.style.setProperty(RESERVED_SPACE_CUSTOM_VAR, newSize + "px");
                }
            }
        });
    };
    #observeCellRemovals = (event) => {
        const removedCellRef = event.detail;
        // Removed the anchor cell
        if (this.cellRefAlignedAtTheTop === removedCellRef) {
            // TODO: StencilJS' algorithm for reconciliate list has several side
            // effects that will trigger the following line when it is not necessary
            // so we are commenting it at the moment to make work the
            // scrollEndContentToTop feature
            // this.cellRefAlignedAtTheTop = null;
        }
        // Removed the last cell
        else if (this.#lastCellRef === removedCellRef) {
            this.#lastCellRef = null;
            this.#reserveSpaceInLastCellToKeepAnchorCellAlignedAtTheStart();
        }
    };
    connectedCallback() {
        // TODO: Investigate this. If we don't add this function call, but we add
        // the class in the Host, the scrollbar is styled, but it shouldn't
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        this.#avoidCLSOnInitialLoad();
        if (this.inverseLoading && !this.autoGrow) {
            this.el.addEventListener("virtualScrollerDidLoad", this.#removeAvoidCLS);
        }
    }
    componentDidRender() {
        if (!this.#contentIsHidden) {
            return;
        }
        if (this.inverseLoading && this.autoGrow) {
            this.#removeAvoidCLS();
        }
    }
    render() {
        const initialLoad = this.loadingState === "initial";
        const hasRecords = this.itemsCount > 0;
        return (h(Host, { key: 'd036f2e41e3c7d1f79d2fff635f55df677f33093', "aria-label": this.accessibleName || undefined, "aria-live": "polite", "aria-busy": initialLoad || this.loadingState === "loading" ? "true" : "false", class: {
                "ch-smart-grid--inverse-loading": hasRecords && this.inverseLoading,
                "ch-smart-grid--data-provider": hasRecords && this.dataProvider && !this.inverseLoading,
                [SCROLLABLE_CLASS]: !this.autoGrow
            } }, initialLoad ? (h("slot", { name: "grid-initial-loading-placeholder" })) : ([
            // TODO: Don't attach the ch-infinite-scroll component if the
            // smart-grid doesn't have an slot "grid-content" in its Light DOM
            // Otherwise, the ch-infinite-scroll will break in runtime
            hasRecords && this.inverseLoading && (h("ch-infinite-scroll", { autoScroll: this.autoScroll, dataProvider: this.dataProvider, disabled: !this.infiniteScrollEnabled, infiniteThresholdReachedCallback: this.#infiniteThresholdReachedCallback, loadingState: this.loadingState, position: "top", threshold: this.threshold })),
            h("slot", { name: hasRecords ? "grid-content" : "grid-content-empty" }),
            hasRecords && this.dataProvider && !this.inverseLoading && (h("ch-infinite-scroll", { dataProvider: true, disabled: !this.infiniteScrollEnabled, infiniteThresholdReachedCallback: this.#infiniteThresholdReachedCallback, loadingState: this.loadingState, threshold: this.threshold }))
        ])));
    }
    static get is() { return "ch-smart-grid"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["smart-grid.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["smart-grid.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "autoGrow": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute defines if the control size will grow automatically,\nto adjust to its content size.\nIf set to `false`, it won't grow automatically and it will show scrollbars\nif the content overflows."
                },
                "attribute": "auto-grow",
                "reflect": false,
                "defaultValue": "false"
            },
            "autoScroll": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"never\" | \"at-scroll-end\"",
                    "resolved": "\"at-scroll-end\" | \"never\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the scroll position will be adjusted when the content size\nchanges when using `inverseLoading = true`.\n  - \"at-scroll-end\": If the scroll is positioned at the end of the content,\n  the chat will maintain the scroll at the end while the content size\n  changes.\n\n - \"never\": The scroll position won't be adjusted when the content size\n  changes."
                },
                "attribute": "auto-scroll",
                "reflect": false,
                "defaultValue": "\"at-scroll-end\""
            },
            "dataProvider": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control has a data provider and therefore must implement a\ninfinite scroll to load data."
                },
                "attribute": "data-provider",
                "reflect": false,
                "defaultValue": "false"
            },
            "inverseLoading": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "When set to `true`, the grid items will be loaded in inverse order, with\nthe first element at the bottom and the \"Loading\" message (infinite-scroll)\nat the top."
                },
                "attribute": "inverse-loading",
                "reflect": false,
                "defaultValue": "false"
            },
            "itemsCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Grid current row count. This property is used in order to be able to\nre-render the Grid every time the Grid data changes.\nIf not specified, then grid empty and loading placeholders may not work\ncorrectly."
                },
                "attribute": "items-count",
                "reflect": false
            },
            "loadingState": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "SmartGridDataState",
                    "resolved": "\"all-records-loaded\" | \"initial\" | \"loading\" | \"more-data-to-fetch\"",
                    "references": {
                        "SmartGridDataState": {
                            "location": "import",
                            "path": "./internal/infinite-scroll/types",
                            "id": "src/components/smart-grid/internal/infinite-scroll/types.ts::SmartGridDataState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the loading state of the grid."
                },
                "attribute": "loading-state",
                "reflect": false,
                "defaultValue": "\"initial\""
            },
            "threshold": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The threshold distance from the bottom of the content to call the\n`infinite` output event when scrolled. The threshold value can be either a\npercent, or in pixels. For example, use the value of `10%` for the\n`infinite` output event to get called when the user has scrolled 10% from\nthe bottom of the page. Use the value `100px` when the scroll is within\n100 pixels from the bottom of the page."
                },
                "attribute": "threshold",
                "reflect": false,
                "defaultValue": "\"10px\""
            }
        };
    }
    static get states() {
        return {
            "infiniteScrollEnabled": {},
            "cellRefAlignedAtTheTop": {}
        };
    }
    static get events() {
        return [{
                "method": "infiniteThresholdReached",
                "name": "infiniteThresholdReached",
                "bubbles": false,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "This Handler will be called every time grid threshold is reached. Needed\nfor infinite scrolling grids."
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "scrollEndContentToPosition": {
                "complexType": {
                    "signature": "(cellId: string, options: { position: \"start\" | \"end\"; behavior?: ScrollBehavior; }) => Promise<void>",
                    "parameters": [{
                            "name": "cellId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "options",
                            "type": "{ position: \"start\" | \"end\"; behavior?: ScrollBehavior; }",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ScrollBehavior": {
                            "location": "global",
                            "id": "global::ScrollBehavior"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given the cell ID, it position the item at the start or end of the\nscrollbar.\n\nIf `position === \"start\"` it will reserve the necessary space to visualize\nthe item at the start of the `ch-smart-grid` viewport if the content is\nnot large enough.\nThis behavior is the same as the Monaco editor does for reserving space\nwhen visualizing the last lines positioned at the top of the editor.",
                    "tags": []
                }
            },
            "removeScrollEndContentReference": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Removes the cell reference that is aligned at the start of the viewport.\n\nIn other words, removes the reserved space that is used to aligned\n`scrollEndContentToPosition(cellId, { position: \"start\" })`",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "cellRefAlignedAtTheTop",
                "methodName": "cellRefAlignedAtTheTopChanged"
            }];
    }
    static get listeners() {
        return [{
                "name": "virtualItemsChanged",
                "method": "handleVirtualItemsChanged",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
